import React from 'react';
import ReactDOM from 'react-dom';
import { RefreshCw, AlertTriangle } from 'lucide-react';

/**
 * Overlay fullscreen que cobre toda a app do operador quando o gestor troca a operação.
 * Bloqueia interação até o operador confirmar com "Aceitar troca".
 *
 * Props:
 *   troca: { operacao_anterior_codigo, operacao_anterior_nome, operacao_nova_codigo, operacao_nova_nome, registo_id }
 *   onAceitar: () => Promise<void>
 *   submitting: boolean
 */
export default function TrocaPendenteOverlay({ troca, onAceitar, submitting }) {
  if (!troca) return null;
  const overlay = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
      style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(4px)' }}
      data-testid="troca-pendente-overlay"
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 text-center animate-in fade-in zoom-in duration-200">
        <div className="relative mx-auto w-20 h-20 mb-4">
          <div className="absolute inset-0 bg-amber-100 rounded-full animate-pulse"></div>
          <div className="relative flex items-center justify-center w-20 h-20 bg-amber-500 rounded-full">
            <RefreshCw className="h-10 w-10 text-white" />
          </div>
        </div>

        <h2 className="text-2xl font-extrabold text-stone-900 mb-1">
          A sua operação foi trocada
        </h2>
        <p className="text-sm text-stone-600 mb-5">
          O gestor alterou a operação que estava a executar. O tempo já contabilizado mantém-se.
        </p>

        {/* Antes → Depois */}
        <div className="space-y-2 mb-5 text-left">
          <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
            <p className="text-[10px] font-bold tracking-wider text-rose-700 uppercase mb-1">Era</p>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="inline-block px-1.5 py-0.5 rounded bg-rose-200 text-rose-900 font-bold text-sm">
                {troca.operacao_anterior_codigo || '—'}
              </span>
              <span className="text-sm font-semibold text-stone-700">{troca.operacao_anterior_nome}</span>
            </div>
          </div>

          <div className="flex justify-center">
            <span className="text-stone-400 text-xs">▼</span>
          </div>

          <div className="rounded-lg bg-emerald-50 border-2 border-emerald-400 p-3 shadow-sm">
            <p className="text-[10px] font-bold tracking-wider text-emerald-700 uppercase mb-1">Agora é</p>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900 font-bold text-sm">
                {troca.operacao_nova_codigo || '—'}
              </span>
              <span className="text-sm font-semibold text-stone-700">{troca.operacao_nova_nome}</span>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 mb-5 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>Continue a trabalhar normalmente. Os registos seguintes ficarão associados à <strong>operação nova</strong>.</span>
        </div>

        <button
          onClick={onAceitar}
          disabled={submitting}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-lg py-4 rounded-xl shadow-md transition"
          data-testid="aceitar-troca-btn"
        >
          {submitting ? 'A aceitar...' : 'Aceitar e continuar'}
        </button>
      </div>
    </div>
  );
  return ReactDOM.createPortal(overlay, document.body);
}
